import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type PicklistValue } from './client';
import { useScopedSelect } from '@/contexts/ClientScopeContext';

/** PSA Projects — mirrors server/psa/projects.ts + projectRoutes.ts (dev/planning/Projects.md). */

export const PROJECT_STATUS = { inactive: 0, new: 1, p10: 2, p25: 3, p50: 4, complete: 5, p75: 6, pendingCompletion: 8 } as const;
export const TASK_COMPLETE = 5;
/** Workflow order for sorting project statuses (5 Complete is not the highest value). */
export const PROJECT_STATUS_ORDER: Record<number, number> = { 1: 0, 2: 1, 3: 2, 4: 3, 6: 4, 8: 5, 5: 6, 0: 7 };

export interface Progress { pct: number; doneHours: number; totalHours: number; tasks: number; doneTasks: number; started: boolean; allDone: boolean; bucket: number | null }
export interface TaskFlags { overdue: boolean; unassigned: boolean; noEstimate: boolean; noWorkType: boolean; noRole: boolean; doneWithoutTime: boolean; legacyPendingCustomer: boolean }
export interface HygieneCounts { open: number; overdue: number; unassigned: number; noEstimate: number; noWorkType: number; noRole: number; doneWithoutTime: number; legacyPendingCustomer: number }

export interface ProjectTask {
  id: number; number: string; projectId: number; phaseId: number | null; title: string; status: number; done: boolean;
  assigneeId: number | null; assigneeName: string | null; roleId: number | null; workTypeId: number | null; departmentId: number | null;
  start: string | null; end: string | null; estimate: number | null; remaining: number | null; logged: number;
  completedAt: string | null; lastActivity: string | null; flags: TaskFlags; priority: number | null;
}

export interface ProjectRow {
  id: number; number: string; name: string; status: number; type: number; companyId: number; companyName: string | null;
  leadId: number | null; leadName: string | null; department: number | null; start: string | null; end: string | null;
  statusDetail: string | null; contractId: number | null; opportunityId: number | null; lastActivity: string | null;
  completedAt: string | null; estimatedHours: number; loggedHours: number; progress: Progress; suggestedStatus: number | null;
  daysOver: number | null; stale: boolean; readyForPendingCompletion: boolean; hygiene: HygieneCounts;
}

export interface ProjectSettings { autoStatus: 'off' | 'dry-run' | 'on'; requireTimeOnComplete: 'off' | 'nudge' | 'require' }

export interface ProjectsResponse { projects: ProjectRow[]; fetchedAt: string; settings: ProjectSettings }

export interface ProjectPhase { id: number; title: string; description: string | null; parentId: number | null; start: string | null; due: string | null; number: string | null }
export interface ProjectNote { id: number; title: string; description: string; noteType: number; publish: number; createdAt: string; authorId: number | null; authorName: string | null; workflow: boolean }
export interface ProjectTime { id: number; taskId: number | null; resourceId: number; name: string; hours: number; date: string | null; summary: string | null; internal: string | null }
export interface ActivityRow { id: number; projectId: number; taskId: number | null; userName: string; action: string; detail: Record<string, unknown> | null; at: string }
export interface StatusLogRow { id: number; projectId: number; projectName: string; fromStatus: number; toStatus: number; progress: number; mode: string; trigger: string; error: string | null; at: string }

export interface ProjectDetailResponse {
  project: ProjectRow & { description: string };
  phases: ProjectPhase[];
  tasks: ProjectTask[];
  secondary: { id: number; taskId: number; resourceId: number; roleId: number; name: string }[];
  predecessors: { id: number; predecessorId: number; successorId: number; lagDays: number }[];
  notes: ProjectNote[];
  time: ProjectTime[];
  tickets: { id: number; number: string; title: string; status: number; createdAt: string }[];
  activity: ActivityRow[];
  statusLog: StatusLogRow[];
  errors: Record<string, string>;
  settings: ProjectSettings;
}

export interface TaskDetailResponse {
  task: ProjectTask & { description: string };
  project: { id: number; name: string; number: string; companyId: number } | null;
  notes: { id: number; title: string; description: string; createdAt: string; authorName: string | null }[];
  time: { id: number; name: string; hours: number; date: string | null; summary: string | null; internal: string | null }[];
  secondary: { id: number; resourceId: number; roleId: number; name: string }[];
}

export type MyTask = ProjectTask & { secondary: boolean; project: { id: number; name: string; number: string; companyId: number; companyName: string | null } | null };

export interface PersonRole { roleId: number; roleName: string; departmentId: number; isDefault: boolean }
export interface ProjectPerson { id: number; name: string; email: string; roles: PersonRole[]; defaultRoleId: number | null }
export interface ProjectPeople { people: ProjectPerson[]; roleNames: Record<string, string>; workTypes: { id: number; name: string }[] }

export interface ProjectPicklists {
  projectStatus: PicklistValue[]; projectType: PicklistValue[]; department: PicklistValue[];
  /** Offered statuses — never Complete-Pending Customer (21). */
  taskStatus: PicklistValue[];
  /** For labelling legacy rows. */
  taskStatusAll: PicklistValue[];
  taskPriority: PicklistValue[]; taskDepartment: PicklistValue[];
  workTypes: { id: number; name: string }[]; roleNames: Record<string, string>;
  /** Admin setting psa.defaultTaskWorkType (Professional Services/AD-HOC). */
  defaultWorkType: number;
}

const KEY = ['psa-projects'] as const;

/** Every Client/Internal project with roll-ups. `scoped`: narrow to the header client picker. */
export function useProjects(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<ProjectsResponse>(opts.scoped, (d, inScope) => ({ ...d, projects: d.projects.filter((p) => inScope(p.companyId)) }));
  return useQuery({ queryKey: [...KEY, 'list'], queryFn: () => api.get<ProjectsResponse>('/api/psa/projects'), staleTime: 60_000, select });
}

export function useProject(id: number | undefined) {
  return useQuery({ queryKey: [...KEY, 'detail', id], enabled: id != null, queryFn: () => api.get<ProjectDetailResponse>(`/api/psa/projects/${id}`), staleTime: 30_000 });
}

export function useProjectTask(taskId: number | null | undefined) {
  return useQuery({ queryKey: [...KEY, 'task', taskId], enabled: taskId != null, queryFn: () => api.get<TaskDetailResponse>(`/api/psa/projects/tasks/${taskId}`), staleTime: 30_000 });
}

export function useMyProjectTasks() {
  return useQuery({ queryKey: [...KEY, 'mine'], queryFn: () => api.get<{ items: MyTask[]; today: string }>('/api/psa/projects/tasks/mine'), staleTime: 60_000 });
}

export function useProjectPeople() {
  return useQuery({ queryKey: [...KEY, 'people'], queryFn: () => api.get<ProjectPeople>('/api/psa/projects/people'), staleTime: 30 * 60_000 });
}

export function useProjectPicklists() {
  return useQuery({ queryKey: [...KEY, 'picklists'], queryFn: () => api.get<ProjectPicklists>('/api/psa/projects/picklists'), staleTime: 60 * 60_000 });
}

export function useProjectStatusLog() {
  return useQuery({ queryKey: [...KEY, 'status-log'], queryFn: () => api.get<{ mode: string; items: StatusLogRow[] }>('/api/psa/projects/status-log'), staleTime: 30_000 });
}

/** Any project write invalidates every projects query (lists, detail, my tasks). */
function useProjectMutation<V, R = unknown>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: KEY }) });
}

export interface TaskPatch {
  title?: string; description?: string; status?: number; phaseID?: number | null;
  assignedResourceID?: number | null; assignedResourceRoleID?: number | null;
  start?: string | null; end?: string | null; estimatedHours?: number; remainingHours?: number;
  billingCodeID?: number; departmentID?: number; priorityLabel?: number | null;
}

export const useUpdateTask = () => useProjectMutation(({ taskId, patch }: { taskId: number; patch: TaskPatch }) => api.patch(`/api/psa/projects/tasks/${taskId}`, patch));
export const useCreateTask = () => useProjectMutation(({ projectId, task }: { projectId: number; task: TaskPatch & { title: string } }) => api.post<{ id: number | null }>(`/api/psa/projects/${projectId}/tasks`, task));
export const useBulkTasks = () => useProjectMutation((body: { taskIds: number[]; fields?: TaskPatch; shiftDays?: number }) =>
  api.post<{ updated: number; failed: number; results: { id: number; ok: boolean; error?: string }[] }>('/api/psa/projects/tasks/bulk', body));
export const useAddTaskNote = () => useProjectMutation(({ taskId, description }: { taskId: number; description: string }) => api.post(`/api/psa/projects/tasks/${taskId}/notes`, { description }));
export interface LogTaskTime { hoursWorked: number; summaryNotes?: string; internalNotes?: string; startDateTime?: string; endDateTime?: string; date?: string; complete?: boolean }
export const useLogTaskTime = () => useProjectMutation(({ taskId, body }: { taskId: number; body: LogTaskTime }) =>
  api.post<{ ok: boolean; completed: boolean; completeError: string | null }>(`/api/psa/projects/tasks/${taskId}/time`, body));
export const useAddSecondary = () => useProjectMutation(({ taskId, resourceId, roleId }: { taskId: number; resourceId: number; roleId?: number }) => api.post(`/api/psa/projects/tasks/${taskId}/secondary-resources`, { resourceId, roleId }));
export const useRemoveSecondary = () => useProjectMutation(({ taskId, linkId }: { taskId: number; linkId: number }) => api.del(`/api/psa/projects/tasks/${taskId}/secondary-resources/${linkId}`));
export const usePostProjectUpdate = () => useProjectMutation(({ projectId, ...body }: { projectId: number; status?: number; statusDetail?: string; note?: string }) => api.post(`/api/psa/projects/${projectId}/update`, body));
export const useUpdateProject = () => useProjectMutation(({ projectId, patch }: { projectId: number; patch: Record<string, unknown> }) => api.patch(`/api/psa/projects/${projectId}`, patch));
export const useAddProjectNote = () => useProjectMutation(({ projectId, description }: { projectId: number; description: string }) => api.post(`/api/psa/projects/${projectId}/notes`, { description }));
export const useCreatePhase = () => useProjectMutation(({ projectId, ...body }: { projectId: number; title: string; start?: string | null; due?: string | null; parentPhaseID?: number | null }) => api.post(`/api/psa/projects/${projectId}/phases`, body));
export const useShiftPhase = () => useProjectMutation(({ phaseId, projectId, days }: { phaseId: number; projectId: number; days: number }) =>
  api.post<{ ok: boolean; tasksMoved: number; errors: string[] }>(`/api/psa/projects/phases/${phaseId}/shift`, { projectId, days }));
export const useRunStatusSweep = () => useProjectMutation(() => api.post<{ mode: string; results: unknown[] }>('/api/psa/projects/status-sweep', {}));
